"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TrendingUp, X } from "lucide-react";
import { api } from "@/lib/api";

const DISMISS_KEY = "granbay.pi_banner_dismissed";

/**
 * EPIC-005 STORY-501 — discoverability CTA for the Popular Investor
 * application. Phase-1 entry was only on /pi/leaderboard's empty
 * state; founder feedback 2026-04-30 was that the surface felt
 * "removed for users". This banner makes the application path
 * visible from /home for any user without a pending or approved
 * application.
 *
 * Hidden when:
 *   - user has any application in `pending` (their bid is in flight)
 *   - user has any application in `approved` (they're already a PI)
 *   - user dismissed the banner (localStorage flag, this device only)
 *
 * Shown otherwise (no applications at all, or last one was rejected/
 * withdrawn — in which case re-applying is welcome).
 */
export function PiApplicationBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (typeof window !== "undefined") {
      if (window.localStorage.getItem(DISMISS_KEY) === "1") {
        return;
      }
    }
    void api
      .listMyPiApplications()
      .then((apps) => {
        if (cancelled) return;
        const blocked = apps.some(
          (a) => a.status === "pending" || a.status === "approved",
        );
        if (!blocked) setVisible(true);
      })
      .catch(() => {
        // Endpoint failure is non-fatal — leave banner hidden rather
        // than nag the user with a CTA we can't trust.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!visible) return null;

  return (
    <div
      className="mb-3 flex items-start gap-3 rounded-xl border p-4"
      style={{
        borderColor:
          "color-mix(in srgb, var(--color-brand) 35%, var(--color-border))",
        background:
          "color-mix(in srgb, var(--color-brand) 6%, var(--color-surface))",
      }}
    >
      <div
        className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full"
        style={{
          background:
            "color-mix(in srgb, var(--color-brand) 18%, transparent)",
          color: "var(--color-brand)",
        }}
      >
        <TrendingUp size={18} strokeWidth={2} aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-[var(--color-text)]">
          {t("piBanner.title")}
        </p>
        <p className="mt-1 text-[12px] text-[var(--color-text2)]">
          {t("piBanner.body")}
        </p>
        <Link
          href="/pi/apply"
          className="mt-2 inline-flex h-8 items-center justify-center rounded-md px-3 text-[12px] font-semibold text-white transition-opacity hover:opacity-90"
          style={{ background: "var(--color-brand)" }}
        >
          {t("piBanner.cta")}
        </Link>
      </div>
      <button
        type="button"
        onClick={() => {
          if (typeof window !== "undefined") {
            window.localStorage.setItem(DISMISS_KEY, "1");
          }
          setVisible(false);
        }}
        aria-label={t("piBanner.dismiss")}
        className="flex-shrink-0 rounded p-1 text-[var(--color-text3)] transition-colors hover:text-[var(--color-text)]"
      >
        <X size={14} strokeWidth={2} aria-hidden="true" />
      </button>
    </div>
  );
}
